import {
  Component,
  Input,
  Output,
  EventEmitter,
  OnInit,
  OnDestroy,
  forwardRef,
  ViewChild,
  AfterViewInit,
} from '@angular/core';
import { ControlValueAccessor, NG_VALUE_ACCESSOR } from '@angular/forms';
import { Subject, takeUntil } from 'rxjs';
import { ThemeLayoutService } from 'src/app/@theme/services/theme-layout.service';
import { BaseService } from 'src/app/tabms/services/base/base.service';
import { RTL } from 'src/app/@theme/const';
import { Calendar } from 'primeng/calendar';
import { TabConst } from 'src/app/tabms/tab-consts';
import { FieldDicHelperService } from 'src/app/tabms/services/global/field-dic-helper.service';
import { ActivatedRoute } from '@angular/router';

@Component({
  selector: 'app-month-picker',
  templateUrl: './month-picker.component.html',
  styleUrls: ['./month-picker.component.scss'],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => MonthPickerComponent),
      multi: true,
    },
  ],
})
export class MonthPickerComponent
  implements OnInit, OnDestroy, AfterViewInit, ControlValueAccessor
{
  @Input() fieldName: string;
  @Input() validate: boolean = true;
  @Input() lang: string;
  @Input() size: 'default' | 'compact' | 'xs' = TabConst.DEFAULTSIZE;
  @Input() customStyle: string;
  @Output() monthSelected = new EventEmitter<number>();
  @ViewChild('monthCalendar') monthCalendar: Calendar;

  placeholder: string;
  systemMenuId: string;
  currentLang: string;
  selectedDate: Date | null = null;
  private destroy$ = new Subject<void>();

  private onChange = (value: any) => {};
  private onTouched = () => {};
  private isWritingValue = false;

  constructor(
    private baseService: BaseService,
    private themeLayoutService: ThemeLayoutService,
    private fieldDicHelperService: FieldDicHelperService,
    private activateRoute: ActivatedRoute
  ) {
    this.activateRoute.params.pipe(takeUntil(this.destroy$)).subscribe({
      next: (params) => (this.systemMenuId = params['id']),
    });

    this.currentLang = this.baseService.getCurrentLanguage();

    this.themeLayoutService.directionChange
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (dir) => {
          this.currentLang = dir === RTL ? 'ar' : 'en';
          this.loadFieldData();
        },
      });
  }

  ngOnInit(): void {
    this.loadFieldData();

    this.baseService.createFormDisplay
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (res) => {
          if (res?.show && res?.id === null) {
            this.loadFieldData();
          }
        },
      });
  }

  ngAfterViewInit(): void {}

  // ✅ لما المستخدم يختار الشهر
  onDateSelected(): void {
    if (this.selectedDate) {
      const month = this.selectedDate.getMonth() + 1;
      this.monthSelected.emit(month);

      // 🔥 لازم ما ندخلش فى loop
      if (!this.isWritingValue) {
        this.onChange(month);
        this.onTouched();
      }
    } else {
      // لما المستخدم يفضى القيمة
      this.onChange(null);
      this.onTouched();
    }
  }

  // ✅ ييجى من الـ Form لما يتغير من بره
  writeValue(value: any): void {
    this.isWritingValue = true;
    // console.log('Month picker writeValue called with:', value);

    if (value === null || value === undefined || value === '0' || value === 0) {
      this.selectedDate = null;
    } else {
      const currentYear = new Date().getFullYear();

      if (typeof value === 'number') {
        this.selectedDate = new Date(currentYear, value - 1, 1);
      } else if (value instanceof Date) {
        this.selectedDate = value;
      }
    }

    this.isWritingValue = false;
  }

  registerOnChange(fn: any): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: any): void {
    this.onTouched = fn;
  }

  setDisabledState(isDisabled: boolean): void {
    if (isDisabled) {
      this.monthCalendar?.inputfieldViewChild?.nativeElement?.setAttribute('disabled', 'true');
    } else {
      this.monthCalendar?.inputfieldViewChild?.nativeElement?.removeAttribute('disabled');
    }
  }

  private loadFieldData() {
    this.fieldDicHelperService
      .getFieldData(this.fieldName, this.systemMenuId, this.validate, null, this.currentLang)
      .pipe(takeUntil(this.destroy$))
      .subscribe(({ placeholder, defaultValue, type, disabled }) => {
        this.placeholder = placeholder;

        if (defaultValue !== null && defaultValue !== undefined) {
          this.writeValue(defaultValue);
          this.onChange(defaultValue);
        }

        if (disabled) {
          this.setDisabledState(true);
        }
      });
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }
}
